import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import ExamSession from "@/components/candidate/ExamSession";
import { examCandidateAPI } from "@/services/api";
import config from "../../config/config";

const ExamScreen = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch exam session details to verify access
  const {
    data: sessionData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["exam-session-verify", sessionId],
    queryFn: () => {
      if (!sessionId || sessionId === "undefined") {
        throw new Error("Invalid session ID");
      }
      return examCandidateAPI.getExamSession(sessionId);
    },
    enabled: !!sessionId && sessionId !== "undefined",
    retry: 1,
  });

  // Event listeners for browser closing/refreshing
  useEffect(() => {
    if (!sessionId || sessionId === "undefined") {
      navigate("/candidate");
      return;
    }

    // Block refresh and close
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = "";

      // Show warning message
      return "Are you sure you want to leave? Exiting may disqualify you from the exam.";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Close event
    const handleUnload = () => {
      // This won't have time to complete on page close, but we'll try
      try {
        navigator.sendBeacon(
          `${config.API_URL}/exam-candidate/session/${sessionId}/proctor`,
          JSON.stringify({
            type: "warning",
            details: "Browser was closed during exam",
            timestamp: new Date().toISOString(),
          })
        );
      } catch (error) {
        console.error("Error sending close event:", error);
      }
    };

    window.addEventListener("unload", handleUnload);

    // Block right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", handleContextMenu);

    // Block keyboard shortcuts
    const handleKeyDown = (e) => {
      // Block: Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+P, F12, Alt+Tab
      if (
        (e.ctrlKey &&
          (e.key === "c" || e.key === "v" || e.key === "x" || e.key === "p")) ||
        e.key === "F12" ||
        (e.altKey && e.key === "Tab")
      ) {
        e.preventDefault();
        toast({
          title: "Action Blocked",
          description: "This keyboard shortcut is disabled during the exam",
          variant: "destructive",
        });
        return false;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sessionId, toast, navigate]);

  // Redirect if session is invalid
  useEffect(() => {
    if (error) {
      console.error("Session error:", error);
      toast({
        title: "Access Denied",
        description:
          "You don't have access to this exam session or it has expired.",
        variant: "destructive",
      });

      // Navigate back to candidate dashboard
      navigate("/candidate");
    }
  }, [error, navigate, toast]);

  if (!sessionId || sessionId === "undefined") {
    return (
      <div className="container py-10">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Invalid session ID. Please start the exam again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-4rem)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary border-t-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-10">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.response?.data?.message ||
              "Failed to load exam session. Please contact an administrator."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Pass the session data to ExamSession component
  return <ExamSession sessionData={sessionData?.data} />;
};

export default ExamScreen;
